import type { Resource } from '@viet-parenting/supabase/types';
import { pick, t } from '../i18n';
import {
  ACCENT_LABEL_KEY,
  ACCESS_LABEL_KEY,
  AGE_LABEL_KEY,
  CONTENT_LABEL_KEY,
  TYPE_LABEL_KEY,
} from '../resource-labels';
import PhotoCarousel from './PhotoCarousel';
import { BackLink, Pill, ReportLink, SectionLabel, StickyCta, SummaryRow } from './parts';
import { ExternalLinkIcon, LinesIcon, TableIcon } from './icons';
import type { DetailContext } from './types';

interface Props {
  resource: Resource;
  ctx: DetailContext;
  /**
   * The badge naming where this resource belongs. The global route shows a
   * globe and "Global"; a country route shows that country's flag and name.
   */
  origin?: ResourceOrigin;
}

export interface ResourceOrigin {
  /** Lowercase ISO code, resolved against the public site's /flags assets. */
  flagCode?: string;
  label: string;
}

export default function ResourceDetail({ resource, ctx, origin }: Props) {
  const { locale } = ctx;
  const name = pick(locale, resource.name_vi, resource.name_en);
  const description = pick(locale, resource.description_vi ?? '', resource.description_en);

  const updatedLabel = new Date(resource.updated_at).toLocaleDateString(
    locale === 'vi' ? 'vi-VN' : 'en-US',
    { month: 'long', year: 'numeric' },
  );

  const photos = resource.photos ?? [];
  const visitLabel = t(locale, 'visit_resource');

  const visitButton = resource.resource_url ? (
    <a
      className="vp-btn vp-btn-primary"
      href={resource.resource_url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={visitLabel}
    >
      <ExternalLinkIcon />
      <span className="vp-sticky-label">{visitLabel}</span>
    </a>
  ) : null;

  return (
    <>
      <div className="vp-page">
        <BackLink ctx={ctx} label={t(locale, 'resources')} />

        <div className="vp-listing-header">
          <h1 className="vp-listing-name" data-pagefind-meta="title">
            {name}
          </h1>
          <div className="vp-header-badges">
            <span className="vp-location-badge">
              {origin?.flagCode ? (
                <img
                  src={`/flags/${origin.flagCode.toLowerCase()}.svg`}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                  className="h-4 w-auto rounded-[2px]"
                />
              ) : (
                <span aria-hidden="true">🌐</span>
              )}
              {origin?.label ?? t(locale, 'global')}
            </span>
            {resource.types.map((tp) => (
              <span className="vp-location-badge vp-badge-pink" key={tp}>
                {t(locale, TYPE_LABEL_KEY[tp])}
              </span>
            ))}
          </div>
        </div>

        <div className="vp-detail-grid">
          <PhotoCarousel photos={photos} name={name} ctx={ctx} />

          {description && (
            <div className="vp-card vp-card-about">
              <SectionLabel icon={<LinesIcon />}>
                {locale === 'vi' ? 'Giới thiệu' : 'About'}
              </SectionLabel>
              <div className="vp-rich" dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          )}

          <div className="vp-card vp-card-summary">
            {visitButton && (
              <div className="vp-cta-row" data-vp-cta-row>
                {visitButton}
              </div>
            )}

            <SectionLabel icon={<TableIcon />}>
              {locale === 'vi' ? 'Thông tin nhanh' : 'Quick Summary'}
            </SectionLabel>

            <table className="vp-summary-table">
              <tbody>
                {resource.types.length > 0 && (
                  <SummaryRow label={t(locale, 'filter_type')}>
                    {resource.types.map((tp) => (
                      <Pill tone="mint" key={tp}>
                        {t(locale, TYPE_LABEL_KEY[tp])}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {resource.age_groups.length > 0 && (
                  <SummaryRow label={t(locale, 'filter_age_group')}>
                    {resource.age_groups.map((a) => (
                      <Pill tone="pink" key={a}>
                        {t(locale, AGE_LABEL_KEY[a])}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {resource.contents.length > 0 && (
                  <SummaryRow label={t(locale, 'filter_content')}>
                    {resource.contents.map((c) => (
                      <Pill tone="yellow" key={c}>
                        {t(locale, CONTENT_LABEL_KEY[c])}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {resource.accesses.length > 0 && (
                  <SummaryRow label={t(locale, 'filter_access')}>
                    {resource.accesses.map((ac) => (
                      <Pill tone="blue" key={ac}>
                        {t(locale, ACCESS_LABEL_KEY[ac])}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {resource.accents.length > 0 && (
                  <SummaryRow label={t(locale, 'filter_accent')}>
                    {resource.accents.map((ac) => (
                      <Pill tone="yellow" key={ac}>
                        {t(locale, ACCENT_LABEL_KEY[ac])}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
              </tbody>
            </table>

            <div className="vp-verified-row">
              <span className="vp-verified-icon" aria-hidden="true">
                ✅
              </span>
              <span>
                {locale === 'vi'
                  ? `Đã xác minh bởi Viet Parenting · Cập nhật ${updatedLabel}`
                  : `Verified by Viet Parenting · Last updated ${updatedLabel}`}
              </span>
              <ReportLink ctx={ctx} className="ml-auto" />
            </div>
          </div>
        </div>
      </div>

      {visitButton && <StickyCta>{visitButton}</StickyCta>}
    </>
  );
}
